import { Row, Col, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import messages from "messages";
import { Fragment, useEffect } from "react";
// constants
import { TWO_WAYS, BENEFITS, EMERGENCY_LINES } from "./constants";
import { GENERAL_ROUTES } from "constants/generalRoutes";
// components
import { ReactComponent as ArrowRight16 } from "assets/svg/arrow-right-16.svg";
import { ReactComponent as ThreeLines } from "assets/svg/three-lines.svg";
import ReadyToMatchForm from "./components/ready-to-match-form";
import ProvidersList from "./components/providers-list";
// styles
import styles from "./styles.module.scss";
// utils
import getClasses from "utils/getClasses";
// hooks
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import LazyImage from "components/lazy-image";
import Footer from "components/footer/Footer";
import { AmplitudePageName, track } from "utils/amplitude";
import { useTimeOnPageContext } from "contexts/time-on-page";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "member-landing-page";
const WRITE_HISTORY = "/img/homepage/member-home/write-history.svg";

/**
 * renders client facing lading page
 */
const ClientFacingLandingPage = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => {
    setPage(AMPLITUDE_PAGE_NAME);
  }, [setPage]);

  const { isPhonePortrait } = usePhonePortraitContext();
  return (
    <>
      <main className={styles["client-facing-landing-page"]}>
        {/* section 1 - hero section */}
        <Row
          className={getClasses(
            styles["client-facing-landing-page__row"],
            styles["client-facing-landing-page__row--text-white"],
            styles["client-facing-landing-page__row--hero-section"],
          )}
        >
          <Col
            className={getClasses(
              styles["client-facing-landing-page__col"],
              styles["client-facing-landing-page__col--hero-section-text"],
            )}
          >
            <h1
              className={
                styles["client-facing-landing-page__hero-section-text-title"]
              }
            >
              {messages.supportThatGoes}
            </h1>
            <div
              className={
                styles["client-facing-landing-page__hero-section-text-body"]
              }
            >
              {messages.mentalHealthcareFrom}
            </div>
            <div>
              <Button
                variant={"primary"}
                className={
                  styles["client-facing-landing-page__action-schedule"]
                }
                as={Link}
                to={GENERAL_ROUTES.professionalSearch}
                onClick={() =>
                  track(
                    AMPLITUDE_PAGE_NAME,
                    "schedule-a-session-button",
                    "click",
                  )
                }
              >
                {messages.scheduleASession}
              </Button>
            </div>
          </Col>
          <Col
            className={getClasses(
              styles["client-facing-landing-page__col"],
              styles["client-facing-landing-page__col--hero-section-image"],
            )}
          />
        </Row>
        {/* section 2 - two ways */}
        <Row
          className={getClasses(
            styles["client-facing-landing-page__row"],
            styles["client-facing-landing-page__row--two-ways"],
          )}
        >
          <Row className={styles["client-facing-landing-page__row"]}>
            <div
              className={styles["client-facing-landing-page__two-ways-title"]}
            >
              {messages.twoSimpleWays}
            </div>
          </Row>

          <Row
            className={getClasses(
              styles["client-facing-landing-page__row"],
              styles["client-facing-landing-page__row--two-ways-columns-row"],
            )}
          >
            {TWO_WAYS.map((el, i) => (
              <Fragment key={el.id}>
                <Col
                  className={getClasses(
                    styles["client-facing-landing-page__col"],
                    styles["client-facing-landing-page__col--two-ways-col"],
                  )}
                >
                  <div
                    className={getClasses(
                      styles["client-facing-landing-page__two-ways-col-image"],
                      styles[
                        `client-facing-landing-page__two-ways-col-image--${el.id}`
                      ],
                    )}
                  />
                  <div
                    className={
                      styles["client-facing-landing-page__two-ways-col-title"]
                    }
                  >
                    {el.title}
                  </div>
                  <div
                    className={
                      styles["client-facing-landing-page__two-ways-col-body"]
                    }
                  >
                    {el.body}
                  </div>
                </Col>
                {i === 0 && (
                  <div
                    className={
                      styles["client-facing-landing-page__two-ways-col-divider"]
                    }
                  ></div>
                )}
              </Fragment>
            ))}
          </Row>
        </Row>
        {/* section 3 - history */}
        <Row
          className={getClasses(
            styles["client-facing-landing-page__row"],
            styles["client-facing-landing-page__row--history"],
          )}
        >
          <Col
            className={getClasses(
              styles["client-facing-landing-page__col"],
              styles["client-facing-landing-page__col--history-text"],
            )}
          >
            <div
              className={
                styles["client-facing-landing-page__history-text-title"]
              }
            >
              {messages.weAreOrchid}
            </div>
            <Button
              variant={"link"}
              className={
                styles["client-facing-landing-page__history-text-action"]
              }
              as={Link}
              to={"/about-us"}
              onClick={() =>
                track(AMPLITUDE_PAGE_NAME, "about-us-button", "click")
              }
            >
              <div>{messages.getTheFullStory}</div>
              <ArrowRight16 />
            </Button>
          </Col>
          <Col
            className={getClasses(
              styles["client-facing-landing-page__col"],
              styles["client-facing-landing-page__col--history-image"],
            )}
          >
            <div
              className={
                styles["client-facing-landing-page__history-image-container"]
              }
            >
              {/* <div
              className={styles["client-facing-landing-page__history-image"]}
            /> */}
              <LazyImage src={WRITE_HISTORY} width={469} height={271} />
            </div>
            <div
              className={
                styles["client-facing-landing-page__history-text-container"]
              }
            >
              <div className={styles["client-facing-landing-page__paragraph"]}>
                <div>
                  {messages.historically1}
                  <span
                    className={
                      styles[
                        "client-facing-landing-page__history-text-highlight"
                      ]
                    }
                  >
                    {messages.historically2}
                  </span>{" "}
                  of folks in the U.S. who want mental healthcare never actually
                  get it. <i>(We were shocked, too.)</i>
                </div>
                <br />
                <div>
                  Historically, therapists and psychiatrists have to spend more
                  time with their business software & computers than with you.
                  <br />
                  <i>(The opposite of how it works for our crew.)</i>
                </div>
                <br />
                <div>
                  And historically, only 13% of mental healthcare professionals
                  use techniques backed by evidence as the basis of their
                  practice. <i>(100% of our providers do.)</i>
                </div>
              </div>
            </div>
          </Col>
        </Row>
        {/* section 4 - benefits */}
        <Row
          className={getClasses(
            styles["client-facing-landing-page__row"],
            styles["client-facing-landing-page__row--benefits"],
          )}
        >
          <Row
            className={getClasses(
              styles["client-facing-landing-page__row"],
              styles["client-facing-landing-page__row--benefits-columns-row"],
            )}
          >
            {BENEFITS.map((el, i) => (
              <Col
                key={el.id}
                className={getClasses(
                  styles["client-facing-landing-page__col"],
                  styles["client-facing-landing-page__col--benefits-col"],
                )}
              >
                <div
                  className={getClasses(
                    styles["client-facing-landing-page__benefits-col-image"],
                    styles[
                      `client-facing-landing-page__benefits-col-image--${i + 1}`
                    ],
                  )}
                />
                <div
                  className={
                    styles["client-facing-landing-page__benefits-col-title"]
                  }
                >
                  {el.title}
                </div>
                <div
                  className={
                    styles["client-facing-landing-page__benefits-col-body"]
                  }
                >
                  {el.id === "instantScheduling" ? (
                    <span>
                      Look at your provider's calendar, choose your favorite
                      time, and then show up to your session. <i>That's it</i>.
                      (You can even reschedule via text.)
                    </span>
                  ) : (
                    el.body
                  )}
                </div>
              </Col>
            ))}
          </Row>
          {!isPhonePortrait ? (
            <Row
              className={getClasses(
                styles["client-facing-landing-page__row"],
                styles["client-facing-landing-page__row--benefits-action-row"],
              )}
            >
              <Button
                variant={"primary"}
                className={
                  styles["client-facing-landing-page__action-schedule"]
                }
                as={Link}
                to={GENERAL_ROUTES.professionalSearch}
                onClick={() =>
                  track(
                    AMPLITUDE_PAGE_NAME,
                    "schedule-a-session-button",
                    "click",
                  )
                }
              >
                {messages.scheduleASession}
              </Button>
            </Row>
          ) : (
            <Button
              variant={"primary"}
              className={getClasses(
                "w-100",
                styles["client-facing-landing-page__action-schedule"],
              )}
              as={Link}
              to={GENERAL_ROUTES.professionalSearch}
              onClick={() =>
                track(AMPLITUDE_PAGE_NAME, "schedule-a-session-button", "click")
              }
            >
              {messages.scheduleASession}
            </Button>
          )}
        </Row>
        {/* section 5 - specialists */}
        <Row
          className={getClasses(
            styles["client-facing-landing-page__row"],
            styles["client-facing-landing-page__row--text-white"],
            styles["client-facing-landing-page__row--specialists"],
          )}
        >
          <div
            className={
              styles["client-facing-landing-page__specialists-content"]
            }
          >
            <Row
              className={getClasses(
                "px-4",
                styles["client-facing-landing-page__row"],
                styles[
                  "client-facing-landing-page__row--specialists-title-container"
                ],
              )}
            >
              <div
                className={
                  styles["client-facing-landing-page__specialists-title"]
                }
              >
                {messages.sayHey1}
                <span
                  className={getClasses(
                    "position-relative",
                    styles[
                      "client-facing-landing-page__specialists-title--highlight"
                    ],
                  )}
                >
                  <span
                    className={
                      styles["client-facing-landing-page__three-lines"]
                    }
                  >
                    <ThreeLines />
                  </span>

                  {messages.sayHey2}
                </span>
                {messages.sayHey3}
              </div>
              <div
                className={
                  styles["client-facing-landing-page__specialists-body"]
                }
              >
                Every professional at Orchid has been personally vetted &
                rigorously approved by our team — and we have <i>very</i> high
                standards.
              </div>
            </Row>

            <Row
              className={getClasses(
                styles["client-facing-landing-page__row"],
                styles["client-facing-landing-page__row--specialists-cards"],
                isPhonePortrait &&
                  styles[
                    "client-facing-landing-page__row--specialists-cards--phone"
                  ],
              )}
            >
              <ProvidersList amplitudePageName={AMPLITUDE_PAGE_NAME} />
            </Row>
          </div>
        </Row>

        {/* section 6 - ready to match */}
        <Row
          className={getClasses(
            styles["client-facing-landing-page__row"],
            styles["client-facing-landing-page__row--ready-to-match"],
          )}
        >
          <Row
            className={getClasses(
              styles["client-facing-landing-page__row"],
              styles[
                "client-facing-landing-page__row--ready-to-match-columns-row"
              ],
            )}
          >
            <Col
              className={getClasses(
                styles["client-facing-landing-page__col"],
                styles[
                  "client-facing-landing-page__col--ready-to-match-col-image"
                ],
              )}
            ></Col>

            <Col
              className={getClasses(
                styles["client-facing-landing-page__col"],
                styles[
                  "client-facing-landing-page__col--ready-to-match-col-text"
                ],
              )}
            >
              <Col
                className={getClasses(
                  styles["client-facing-landing-page__col"],
                  styles[
                    "client-facing-landing-page__col--ready-to-match-col-text-container"
                  ],
                )}
              >
                <div
                  className={
                    styles["client-facing-landing-page__ready-to-match-title"]
                  }
                >
                  {messages.readyToMatch}
                </div>
                <div
                  className={
                    styles["client-facing-landing-page__ready-to-match-body"]
                  }
                >
                  {messages.take30Seconds}
                </div>

                <ReadyToMatchForm amplitudePageName={AMPLITUDE_PAGE_NAME} />
                <p
                  className={getClasses(
                    styles["client-facing-landing-page__paragraph"],
                    styles[
                      "client-facing-landing-page__paragraph--ready-to-match-note"
                    ],
                    isPhonePortrait &&
                      styles[
                        "client-facing-landing-page__paragraph--ready-to-match-note--phone"
                      ],
                  )}
                >
                  {messages.psGreatJob}
                </p>
              </Col>
            </Col>
          </Row>
        </Row>

        {/* section 7 - care back */}
        <Row
          className={getClasses(
            styles["client-facing-landing-page__row"],
            styles["client-facing-landing-page__row--care-back"],
          )}
        >
          <Row
            className={getClasses(
              styles["client-facing-landing-page__row"],
              styles[
                "client-facing-landing-page__row--care-back-text-container"
              ],
            )}
          >
            <div
              className={styles["client-facing-landing-page__care-back-title"]}
            >
              Let's put the <i>care</i> back in mental healthcare.
            </div>
            <div
              className={styles["client-facing-landing-page__care-back-body"]}
            >
              {messages.wePartnerWith}
            </div>
          </Row>
          <Row
            className={getClasses(
              styles["client-facing-landing-page__row"],
              styles["client-facing-landing-page__row--care-back-columns-row"],
            )}
          >
            {[
              {
                href: "https://www.ycombinator.com/",
                name: "Y Combinator",
                id: "y-combinator-link",
              },
              { href: "https://nami.org/Home", name: "NAMI", id: "nami-link" },
              {
                href: "https://thefailureball.com/",
                name: "The Failure Ball",
                id: "failure-ball-link",
              },
              {
                href: "https://www.asianmhc.org/",
                name: "Asian Mental Health Collective",
                id: "asian-mental-health-collective-link",
              },
            ].map((el) => (
              <Col
                key={el.name}
                className={getClasses(
                  styles["client-facing-landing-page__col"],
                  styles["client-facing-landing-page__col--care-back-col"],
                )}
                as={"a"}
                target="_blank"
                rel="noopener noreferrer"
                href={el.href}
                aria-label={el.name}
                onClick={() => track(AMPLITUDE_PAGE_NAME, el.id, "click")}
              />
            ))}
          </Row>
        </Row>
        {/* section 8 - emergency */}
        <Row
          className={getClasses(
            styles["client-facing-landing-page__row"],
            styles["client-facing-landing-page__row--emergency"],
          )}
        >
          <Col
            className={getClasses(
              styles["client-facing-landing-page__col"],
              styles["client-facing-landing-page__col--emergency-col-left"],
            )}
          >
            <p
              className={getClasses(
                styles["client-facing-landing-page__paragraph"],
                styles["client-facing-landing-page__paragraph--emergency"],
              )}
            >
              {messages.ifYouHaveAnEmergency1}
              <span
                className={
                  styles["client-facing-landing-page__emergency-text-highlight"]
                }
              >
                {messages.ifYouHaveAnEmergency2}
              </span>
              {messages.ifYouHaveAnEmergency3}
            </p>
          </Col>

          <Col
            className={getClasses(
              styles["client-facing-landing-page__col"],
              styles["client-facing-landing-page__col--emergency-col-right"],
            )}
          >
            {EMERGENCY_LINES.map((el) => (
              <div
                key={el.id}
                className={styles["client-facing-landing-page__emergency-col"]}
              >
                <div
                  className={
                    styles["client-facing-landing-page__emergency-col-title"]
                  }
                >
                  {el.title}
                </div>
                <div
                  className={
                    styles["client-facing-landing-page__emergency-col-text"]
                  }
                >
                  {el.body && <span>{el.body}</span>}

                  <span
                    className={
                      styles[
                        "client-facing-landing-page__emergency-text-highlight"
                      ]
                    }
                  >
                    {el.phone}
                  </span>
                </div>
              </div>
            ))}
          </Col>
        </Row>
      </main>
      <Footer AMPLITUDE_PAGE_NAME={AMPLITUDE_PAGE_NAME} />
    </>
  );
};

export default ClientFacingLandingPage;
